import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth as firebaseAuth, db, functions } from '@/firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import logoImage from '../../../assets/image/logo.jpg';
import RestrictionModal from '@/components/RestrictionModal';
import useLearningRestrictions from '@/hooks/useLearningRestrictions';
import { REQUIRED_EXAM_COURSE } from '@/hooks/useCourseAccess';

interface Quiz {
    id: string;
    question: string;
    explanation: string;
}

interface Shikaku {
    id: string;
    orderId: string;
    name: string;
}

interface QuizAnswerForm {
    quizId: string;
    userAnswer: number | null;
}

export default function QuizPlayPage() {
    const navigate = useNavigate();
    const { userId } = useAuth();
    const auth = useMemo(() => firebaseAuth, []);

    const [shikakus, setShikakus] = useState<Shikaku[]>([]);
    const [selectedShikakuId, setSelectedShikakuId] = useState<string>('');
    const [quizzes, setQuizzes] = useState<Quiz[]>([]);
    const [answers, setAnswers] = useState<QuizAnswerForm[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingShikakus, setLoadingShikakus] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<number | null>(null);
    const [showAbortConfirm, setShowAbortConfirm] = useState(false);
    const { settings } = useLearningRestrictions();
    const [isRestrictionOpen, setIsRestrictionOpen] = useState(false);

    useEffect(() => {
        if (!settings.quizEnabled) {
            setIsRestrictionOpen(true);
        }
    }, [settings.quizEnabled]);

    // Fetch user's orders and shikaku
    useEffect(() => {
        const fetchShikakus = async () => {
            try {
                if (!userId) {
                    console.log('QuizPlay: No userId available');
                    setLoadingShikakus(false);
                    return;
                }
                // Get user's active orders - Query by UserRef (Reference) - same as select-exam
                const userRef = doc(db, 'User', userId);
                console.log('QuizPlay: Fetching orders for UserRef:', userRef.path);
                
                const ordersQuery = query(
                    collection(db, 'Order'),
                    where('UserRef', '==', userRef),
                    where('status', '==', 'active')
                );

                const snapshot = await getDocs(ordersQuery);
                const orderDocs = snapshot.docs;
                console.log('QuizPlay: Orders found:', orderDocs.length);

                if (orderDocs.length === 0) {
                    console.log('QuizPlay: No orders found for user');
                    setShikakus([]);
                    setLoadingShikakus(false);
                    return;
                }

                // Fetch Shikaku details - same as select-exam
                const shikakulData = await Promise.all(
                    orderDocs.map(async (orderDoc: any) => {
                        const orderData = orderDoc.data();

                        if (
                            typeof orderData.course !== 'string' ||
                            orderData.course.trim() !== REQUIRED_EXAM_COURSE
                        ) {
                            return null;
                        }
                        
                        // shikakuId is already a DocumentReference
                        const shikakuRef = orderData.shikakuId;
                        if (!shikakuRef) {
                            console.warn('QuizPlay: Order missing shikakuId:', orderDoc.id);
                            return null;
                        }

                        const shikakuDoc = await getDoc(shikakuRef);
                        if (!shikakuDoc.exists()) {
                            console.warn('QuizPlay: Shikaku not found:', shikakuRef.id);
                            return null;
                        }

                        const shikakuData = shikakuDoc.data() as any;
                        return {
                            id: shikakuDoc.id,
                            orderId: orderDoc.id,
                            name: shikakuData.ShikakuName || shikakuDoc.id
                        };
                    })
                );

                const validShikakus = shikakulData.filter(s => s !== null) as Shikaku[];
                console.log('QuizPlay: Valid shikakus:', validShikakus);
                setShikakus(validShikakus);
            } catch (error) {
                console.error('QuizPlay: Error fetching shikakus:', error);
            } finally {
                setLoadingShikakus(false);
            }
        };

        fetchShikakus();
    }, [userId]);

    // Fetch quizzes when shikakuId is selected
    useEffect(() => {
        if (!selectedShikakuId) return;

        const fetchQuizzes = async () => {
            const user = auth.currentUser;
            if (!user) return;

            setLoading(true);
            try {
                const getQuizzesFunc = httpsCallable(functions, 'getQuizzesForUser');
                const result = await getQuizzesFunc({ shikakuId: selectedShikakuId });

                const quizzesData = (result.data as any).quizzes || [];
                setQuizzes(quizzesData);

                // Initialize answers form
                const initialAnswers = quizzesData.map((q: Quiz) => ({
                    quizId: q.id,
                    userAnswer: null
                }));
                setAnswers(initialAnswers);
                setCurrentQuestionIndex(0);
                setSelectedOption(null);
            } catch (error: any) {
                console.error('Error fetching quizzes:', error);
                alert(`クイズの取得に失敗しました: ${error.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchQuizzes();
    }, [selectedShikakuId]);

    useEffect(() => {
        setSelectedOption(answers[currentQuestionIndex]?.userAnswer ?? null);
    }, [currentQuestionIndex, answers]);

    const handleAnswerChange = (_index: number, value: number) => {
        setSelectedOption(value);
    };

    const handleSubmit = async (finalAnswers: QuizAnswerForm[] = answers) => {
        // Check all answers are provided
        if (finalAnswers.some(a => a.userAnswer === null)) {
            alert('すべての問題に回答してください');
            return;
        }

        const user = auth.currentUser;
        if (!user) return;

        setLoading(true);
        try {
            const submitFunc = httpsCallable(functions, 'submitQuizAnswers');
            const submitResult = await submitFunc({
                shikakuId: selectedShikakuId,
                answers: finalAnswers.map(a => ({
                    quizId: a.quizId,
                    userAnswer: a.userAnswer
                }))
            });

            const resultData = (submitResult.data as any);
            setResult(resultData);
            setSubmitted(true);
            setAnswers(finalAnswers);
        } catch (error: any) {
            console.error('Error submitting answers:', error);
            alert(`回答の送信に失敗しました: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const selectedShikaku = shikakus.find(s => s.id === selectedShikakuId);
    const currentQuestion = useMemo(() => quizzes[currentQuestionIndex], [currentQuestionIndex, quizzes]);
    const totalQuestions = quizzes.length;
    const currentAnswer = selectedOption;
    const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

    const handleNextQuestion = () => {
        if (currentAnswer === null || currentAnswer === undefined) return;

        const updatedAnswers = answers.map((answer, idx) =>
            idx === currentQuestionIndex ? { ...answer, userAnswer: currentAnswer } : answer
        );
        setAnswers(updatedAnswers);

        if (isLastQuestion) {
            handleSubmit(updatedAnswers);
        } else {
            setCurrentQuestionIndex(prev => prev + 1);
            setSelectedOption(null);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#f1f5ff] via-white to-[#f4f7ff] py-10 px-4 flex items-center justify-center">
            <div className="w-full max-w-xl">
                {/* Shikaku Selection */}
                {!selectedShikakuId && (
                    <div className="bg-white shadow-xl rounded-3xl p-8 relative">
                        <button
                            onClick={() => navigate(-1)}
                            className="absolute left-4 top-4 text-sm text-gray-500 hover:text-gray-700 transition"
                        >
                            ＜ 戻る
                        </button>
                        <div className="mb-6 text-center">
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">資格を選択</h2>
                            <p className="text-gray-500 text-sm">受験する資格を選んでください</p>
                        </div>
                        {loadingShikakus ? (
                            <div className="text-center text-gray-500">読み込み中...</div>
                        ) : shikakus.length === 0 ? (
                            <div className="text-center text-gray-500">受験可能な資格がありません</div>
                        ) : (
                            <div className="space-y-3">
                                {shikakus.map(shikaku => (
                                    <button
                                        key={shikaku.id}
                                        onClick={() => setSelectedShikakuId(shikaku.id)}
                                        className="w-full px-4 py-3 rounded-2xl border border-[#d8e4ff] bg-[#f7f9ff] text-[#1f3a93] font-semibold shadow-sm hover:shadow-md hover:border-[#b7c9ff] transition"
                                    >
                                        {shikaku.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Quiz Form */}
                {selectedShikakuId && !submitted && (
                    <div className="bg-white shadow-2xl rounded-[32px] p-8 relative overflow-hidden">
                        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                            <div className="absolute -top-10 -left-10 w-40 h-40 bg-[#f0f4ff] rounded-full" />
                            <div className="absolute -bottom-16 -right-10 w-48 h-48 bg-[#e5f2ff] rounded-full" />
                        </div>

                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-6">
                                <span className="text-sm text-gray-500 font-medium">{selectedShikaku?.name}</span>
                                <button
                                    type="button"
                                    onClick={() => setShowAbortConfirm(true)}
                                    className="text-2xl text-gray-400 hover:text-gray-600 transition"
                                    aria-label="クイズを中止する"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="text-center mb-6">
                                <img src={logoImage} alt="LeanForYourLife logo" className="mx-auto h-10 w-auto object-contain mb-2" />
                                <h1 className="text-2xl font-bold text-gray-900">実力チェック</h1>
                            </div>

                            {loading ? (
                                <div className="text-center py-10 text-gray-500">クイズを読み込み中...</div>
                            ) : quizzes.length === 0 || !currentQuestion ? (
                                <div className="text-center py-10 text-gray-500">クイズがありません</div>
                            ) : (
                                <>
                                    <div className="mb-6 flex items-center justify-between text-sm text-gray-600">
                                        <div className="flex items-center gap-2">
                                            <span className="text-[#1f3a93] font-bold">Q{currentQuestionIndex + 1}</span>
                                        </div>
                                        <span className="font-semibold">{currentQuestionIndex + 1}/{totalQuestions}</span>
                                    </div>

                                    <div className="mb-8 bg-white/80 rounded-2xl border border-[#e7ebf5] p-5 shadow-sm">
                                        <p className="text-gray-800 leading-relaxed text-lg">{currentQuestion.question}</p>
                                    </div>

                                    <div className="flex items-center justify-center gap-8 mb-6">
                                        <button
                                            type="button"
                                            onClick={() => handleAnswerChange(currentQuestionIndex, 1)}
                                            className={`w-20 h-20 rounded-full border-2 flex items-center justify-center text-4xl font-bold transition focus:outline-none ${
                                                currentAnswer === 1
                                                    ? 'bg-[#e7f5ee] border-[#34c38f] text-[#2fa878] shadow-lg'
                                                    : 'bg-white border-[#d7e2f3] text-[#2fa878] hover:border-[#a9d8bd] hover:shadow'
                                            }`}
                                        >
                                            ○
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleAnswerChange(currentQuestionIndex, 0)}
                                            className={`w-20 h-20 rounded-full border-2 flex items-center justify-center text-4xl font-bold transition focus:outline-none ${
                                                currentAnswer === 0
                                                    ? 'bg-[#ffe8ec] border-[#f25f6a] text-[#e53945] shadow-lg'
                                                    : 'bg-white border-[#d7e2f3] text-[#e53945] hover:border-[#f8a5ad] hover:shadow'
                                            }`}
                                        >
                                            ×
                                        </button>
                                    </div>

                                    <button
                                        onClick={handleNextQuestion}
                                        disabled={currentAnswer === null || currentAnswer === undefined || loading}
                                        className={`w-full py-3 rounded-full text-white font-semibold shadow-md transition transform hover:-translate-y-0.5 ${
                                            currentAnswer === null || currentAnswer === undefined || loading
                                                ? 'bg-[#b8c7ff] cursor-not-allowed'
                                                : 'bg-[#5a6ff0] hover:bg-[#4c61d6]'
                                        }`}
                                    >
                                        {isLastQuestion ? '結果を送信' : '次の問題へ進む'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {showAbortConfirm && (
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl shadow-xl p-6 w-80">
                            <p className="text-center text-gray-800 font-semibold mb-6">
                                中止しますか？この結果は保存されません
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => navigate('/quiz')}
                                    className="flex-1 py-2 rounded-full bg-red-500 text-white font-semibold hover:bg-red-600 transition"
                                >
                                    中止する
                                </button>
                                <button
                                    onClick={() => setShowAbortConfirm(false)}
                                    className="flex-1 py-2 rounded-full border border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition"
                                >
                                    中止しない
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Result Screen */}
                {submitted && result && (
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold text-gray-800 mb-2">採点結果</h2>
                            <div className="text-5xl font-bold text-blue-600 mb-2">
                                {result.score}/{result.totalQuestions}
                            </div>
                            <div className="text-2xl text-gray-600">
                                正解率: {result.percentage}%
                            </div>
                        </div>

                        {/* Details */}
                        <div className="space-y-6 mb-8">
                            {result.details && result.details.map((detail: any, index: number) => (
                                <div key={index} className={`border-l-4 p-4 ${detail.isCorrect ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-2xl">
                                            {detail.isCorrect ? '✓' : '✗'}
                                        </span>
                                        <h4 className="font-semibold text-gray-800">第{index + 1}問</h4>
                                    </div>
                                    <p className="text-gray-700 mb-2">{detail.question}</p>
                                    <p className="text-sm text-gray-600 mb-2">
                                        あなたの回答: <span className="font-semibold">{detail.userAnswer === 1 ? '◯' : '✗'}</span>
                                        {!detail.isCorrect && (
                                            <span> (正解: <span className="font-semibold">{detail.correctAnswer === 1 ? '◯' : '✗'}</span>)</span>
                                        )}
                                    </p>
                                    <p className="text-sm text-gray-700"><strong>解説:</strong> {detail.explanation}</p>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-4">
                            <button
                                onClick={() => {
                                    setSelectedShikakuId('');
                                    setSubmitted(false);
                                    setResult(null);
                                    setQuizzes([]);
                                    setAnswers([]);
                                    setSelectedOption(null);
                                }}
                                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                            >
                                もう一度実施
                            </button>
                            <button
                                onClick={() => navigate('/quiz/history')}
                                className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                            >
                                履歴を見る
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <RestrictionModal
                open={isRestrictionOpen}
                message={settings.message}
                actionLabel="学習メニューへ戻る"
                onAction={() => navigate('/learning')}
            />
        </div>
    );
}
